/* ============================================================
   Streetform — global.js
   Original theme JavaScript. No dependencies.
   Handles: sticky header, mobile drawer, search overlay,
   scroll reveal, page transitions, variant picker,
   AJAX add-to-cart, product recommendations.
   ============================================================ */
(function () {
  'use strict';

  const ready = (fn) =>
    document.readyState !== 'loading'
      ? fn()
      : document.addEventListener('DOMContentLoaded', fn);

  /* ---------- Cart toast ---------- */
  function initToast() {
    const style = document.createElement('style');
    style.textContent = `
      #cart-toast {
        position: fixed;
        top: 1.25rem;
        left: 50%;
        transform: translateX(-50%) translateY(-160%);
        background: #111;
        color: #fff;
        padding: 0.9rem 1.75rem;
        border-radius: 4px;
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        z-index: 9999;
        transition: transform 0.35s cubic-bezier(0.34, 1.4, 0.64, 1);
        white-space: nowrap;
        pointer-events: none;
        box-shadow: 0 4px 20px rgba(0,0,0,0.25);
      }
      #cart-toast.is-visible {
        transform: translateX(-50%) translateY(0);
      }
    `;
    document.head.appendChild(style);
  }

  function showToast(msg) {
    let toast = document.getElementById('cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2500);
  }

  /* ---------- Sticky header on scroll-up ---------- */
  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    let lastY = window.scrollY;
    const threshold = 400;

    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        if (y < 10) {
          header.classList.remove('is-scrolled');
        } else if (y > threshold && y < lastY) {
          header.classList.add('is-scrolled');
        } else if (y > lastY) {
          header.classList.remove('is-scrolled');
        }
        lastY = y;
      },
      { passive: true }
    );
  }

  /* ---------- Mobile drawer ---------- */
  function initDrawer() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    if (!toggle || !drawer) return;

    const close = () => {
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      setTimeout(() => (drawer.hidden = true), 350);
    };

    toggle.addEventListener('click', () => {
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      toggle.setAttribute('aria-expanded', 'true');
    });

    drawer.addEventListener('click', (e) => {
      if (e.target === drawer || e.target.closest('[data-menu-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });
  }

  /* ---------- Search overlay ---------- */
  function initSearch() {
    const toggle = document.querySelector('[data-search-toggle]');
    const overlay = document.querySelector('[data-search-overlay]');
    if (!toggle || !overlay) return;

    const input = overlay.querySelector('[data-search-input]');
    const closeBtn = overlay.querySelector('[data-search-close]');

    const open = () => {
      overlay.hidden = false;
      requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        input && input.focus();
      });
    };
    const close = () => {
      overlay.classList.remove('is-open');
      setTimeout(() => (overlay.hidden = true), 350);
    };

    toggle.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    const items = document.querySelectorAll('.reveal');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));
  }

  /* ---------- Page transitions ---------- */
  function initTransitions() {
    if (!document.body.classList.contains('page-transitions-enabled')) return;

    // La animación de entrada la corre el CSS al cargar. Los clics no se
    // interceptan: la navegación debe partir en el mismo instante del clic.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) document.body.classList.remove('is-transitioning');
    });
  }

  /* ---------- Sold-out option pills ---------- */
  function soldOutPillStyles() {
    // Los selectores llevan la clase dos veces a propósito: así ganan
    // especificidad sobre la regla de talla seleccionada de main-product.
    const style = document.createElement('style');
    style.textContent = `
      .option-pill.option-pill--soldout span,
      .option-pill.option-pill--soldout input:checked + span {
        background: rgb(0 0 0 / 0.08);
        color: rgb(0 0 0 / 0.4);
        border-color: rgb(0 0 0 / 0.1);
        cursor: not-allowed;
      }
      .option-pill.option-pill--soldout { cursor: not-allowed; }
      .option-pill.option-pill--soldout input { pointer-events: none; }
    `;
    document.head.appendChild(style);
  }

  /* ---------- Variant picker + AJAX add to cart ---------- */
  function initProductForm() {
    const wrapper = document.querySelector('[data-product]');
    if (!wrapper) return;

    const jsonEl = document.querySelector('[data-product-json]');
    const form = wrapper.querySelector('form[data-product-form], form[action*="/cart/add"]');
    if (!jsonEl || !form) return;

    let variants = [];
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (err) {
      return;
    }

    const idInput = form.querySelector('[data-variant-id]');
    const addBtn = form.querySelector('[data-add-to-cart]');
    const addBtnText = form.querySelector('[data-add-to-cart-text]');
    const optionSets = wrapper.querySelectorAll('[data-option-index]');

    function markSoldOut() {
      optionSets.forEach((set) => {
        const index = Number(set.dataset.optionIndex);
        set.querySelectorAll('[data-option-input]').forEach((input) => {
          const inStock = variants.some(
            (v) => v.options[index] === input.value && v.available
          );
          if (inStock) return;
          input.disabled = true;
          input.closest('.option-pill').classList.add('option-pill--soldout');
        });
      });
    }

    function selectedOptions() {
      return Array.from(optionSets).map((set) => {
        const checked = set.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant(options) {
      return variants.find((v) =>
        options.every((opt, i) => opt === null || v.options[i] === opt)
      );
    }

    function updateState() {
      const variant = findVariant(selectedOptions());
      if (!variant) {
        addBtn.disabled = true;
        addBtnText.textContent = window.themeStrings.unavailable;
        return;
      }
      idInput.value = variant.id;
      addBtn.disabled = !variant.available;
      addBtnText.textContent = variant.available
        ? window.themeStrings.addToCart
        : window.themeStrings.soldOut;

      const priceWrapper = wrapper.querySelector('[data-price-wrapper] .price__current');
      if (priceWrapper && typeof variant.price === 'number') {
        priceWrapper.textContent = formatMoney(variant.price);
      }
    }

    function formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD',
      }).format(cents / 100);
    }

    soldOutPillStyles();
    markSoldOut();

    optionSets.forEach((set) =>
      set.addEventListener('change', updateState)
    );

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      addBtn.disabled = true;
      try {
        const res = await fetch(window.themeRoutes.cartAdd + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(idInput.value), quantity: 1 }),
        });
        if (!res.ok) throw new Error('add failed');

        addBtnText.textContent = window.themeStrings.added;
        showToast('✓  Agregado al carrito');
        await refreshCartCount();
        setTimeout(() => {
          addBtnText.textContent = window.themeStrings.addToCart;
          addBtn.disabled = false;
        }, 1600);
      } catch (err) {
        addBtnText.textContent = window.themeStrings.cartError;
        addBtn.disabled = false;
      }
    });
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(window.themeRoutes.cart + '.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
    } catch (err) {
      /* silent */
    }
  }

  /* ---------- Product recommendations ---------- */
  function initRecommendations() {
    const el = document.querySelector('[data-recommendations]');
    if (!el || el.querySelector('.product-grid')) return;

    fetch(el.dataset.url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initReveal();
        }
      })
      .catch(() => {});
  }

  /* ---------- AJAX cart remove ---------- */
  function initCartRemove() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-remove-line]');
      if (!btn) return;
      e.preventDefault();
      const line = parseInt(btn.dataset.removeLine, 10);
      const cartItem = btn.closest('.cart-item');
      btn.style.opacity = '0.4';
      btn.disabled = true;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
        const cart = await res.json();
        if (cart.item_count === 0) {
          location.reload();
          return;
        }
        if (cartItem) cartItem.remove();
        const subtotalEl = document.querySelector('[data-cart-subtotal]');
        if (subtotalEl) {
          subtotalEl.textContent = new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'CLP',
            minimumFractionDigits: 0,
          }).format(cart.total_price / 100);
        }
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
        });
        document.querySelectorAll('[data-remove-line]').forEach((b, idx) => {
          b.dataset.removeLine = idx + 1;
        });
      } catch (err) {
        btn.style.opacity = '';
        btn.disabled = false;
      }
    });
  }

  ready(() => {
    initToast();
    initHeader();
    initDrawer();
    initSearch();
    initReveal();
    initTransitions();
    initProductForm();
    initRecommendations();
    initCartRemove();
  });
})();